import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Table,
  InputNumber,
  DatePicker,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  message,
  Modal,
  Tag,
  Tooltip,
  AutoComplete,
  Switch
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SendOutlined,
  SaveOutlined,
  EyeOutlined,
  PrinterOutlined,
  CalculatorOutlined,
  UserOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../../../../services/apiService';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  taxId?: string;
}

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  taxRate: number;
  unit: string;
  category: string;
  sku: string;
  alternateSkuList?: string[]; // 添加备用SKU列表
}

interface InvoiceItem {
  id: string;
  productId: string;
  productName: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
  amount: number;
}

interface Invoice {
  id?: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  issueDate: string;
  dueDate: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  items: InvoiceItem[];
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  notes?: string;
  terms?: string;
  paymentTerms?: string;
}

interface InvoiceBuilderProps {
  onCancel?: () => void;
  onSaved?: () => void;
  editId?: string;
}

const InvoiceBuilder: React.FC<InvoiceBuilderProps> = ({ onCancel, onSaved, editId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [invoice, setInvoice] = useState<Invoice>({
    invoiceNumber: '',
    customerId: '',
    customerName: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    status: 'draft',
    items: [],
    subtotal: 0,
    taxAmount: 0,
    discountAmount: 0,
    totalAmount: 0
  });
  const [isEdit, setIsEdit] = useState(false);

  // 簡單的記憶體內快取，避免每次開彈窗都重抓
  // 注意：頁面刷新會清空；若需跨頁持久化可改用 localStorage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalAny = globalThis as any;
  if (!globalAny.__invoiceCustomersCache) globalAny.__invoiceCustomersCache = null;
  if (!globalAny.__invoiceProductsCache) globalAny.__invoiceProductsCache = null;

  const [customers, setCustomers] = useState<Customer[]>(globalAny.__invoiceCustomersCache || []);
  const [products, setProducts] = useState<Product[]>(globalAny.__invoiceProductsCache || []);
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // 初始化：載入客戶/產品；若 localStorage 有 invoice.editId 則回填為編輯模式
  useEffect(() => {
    const init = async () => {
      try {
        const companyId = localStorage.getItem('companyId') || localStorage.getItem('currentCompanyId') || undefined;
        const effectiveEditId = editId || localStorage.getItem('invoice.editId');
        // 先行設置模式，避免初始渲染顯示成 Create
        setIsEdit(Boolean(effectiveEditId));

        // 若是新增模式，先準備預設單號就可以渲染；編輯模式等詳情載入後再設置
        if (!effectiveEditId) {
          const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
          setInvoice(prev => ({ ...prev, invoiceNumber }));
          form.setFieldValue('invoiceNumber', invoiceNumber);
        }

        // 並行：優先載入詳情，客戶/產品採延遲或快取
        const fetchCustomers = async () => {
          if (globalAny.__invoiceCustomersCache) {
            setCustomers(globalAny.__invoiceCustomersCache);
            return;
          }
          try {
            const cust = await apiService.getCustomersV2({ page: 1, limit: 200, ...(companyId ? { companyId } : {}) });
            const rowsC: any[] = Array.isArray(cust?.data) ? cust.data : (Array.isArray(cust?.customers) ? cust.customers : (Array.isArray(cust) ? cust : []));
            const mappedCustomers: Customer[] = (rowsC||[]).map((c:any)=>({ id: String(c.id||c.customerId), name: c.name||c.customerName, email: c.email||'', phone: c.phone||'', address: c.address||'', taxId: c.taxId||'' }));
            globalAny.__invoiceCustomersCache = mappedCustomers;
            setCustomers(mappedCustomers);
          } catch { /* ignore */ }
        };

        const fetchProducts = async () => {
          // 對於編輯模式：若有現有項目，先把現有產品放入下拉，避免空白
          if (effectiveEditId && Array.isArray((invoice as any)?.items) && (invoice as any).items.length > 0) {
            const existing: Product[] = (invoice as any).items.map((it: any) => ({
              id: String(it.productId),
              name: it.product?.itemName || it.productName || '',
              description: it.description || '',
              price: Number(it.unitPrice || 0),
              taxRate: 0,
              unit: '',
              category: ''
            }));
            setProducts(existing);
          }
          if (globalAny.__invoiceProductsCache) {
            setProducts(prev => prev.length ? prev : globalAny.__invoiceProductsCache);
            return;
          }
          try {
            const prod = await apiService.getProductsV2({ page: 1, limit: 200, ...(companyId ? { companyId } : {}) });
            const rowsP: any[] = Array.isArray(prod?.products) ? prod.products : (Array.isArray(prod?.data?.products) ? prod.data.products : (Array.isArray(prod?.data) ? prod.data : (Array.isArray(prod) ? prod : [])));
            const mappedProducts: Product[] = (rowsP||[]).map((p:any)=>({ 
              id: String(p.id||p.productId), 
              name: p.itemName||p.name,
              sku: p.itemCode || p.sku,
              description: p.description||'', 
              price: Number(p.unitPrice||p.price||0), 
              taxRate: Number(p.taxRate||0), 
              unit: p.baseUOM||p.uom||'', 
              category: p.category||'',
              alternateSkuList: p.alternateSkuList || [] // 添加备用SKU列表
            }));
            globalAny.__invoiceProductsCache = mappedProducts;
            setProducts(prev => prev.length ? prev : mappedProducts);
          } catch { /* ignore */ }
        };

        fetchCustomers();
        fetchProducts();

        // 編輯模式：讀取詳情回填
        if (effectiveEditId) {
          const data: any = await apiService.getInvoiceById(effectiveEditId);
          form.setFieldsValue({
            invoiceNumber: data.invoiceNo || data.invoiceNumber,
            issueDate: data.createdAt ? dayjs(data.createdAt) : undefined,
            dueDate: data.dueDate ? dayjs(data.dueDate) : undefined,
            customerId: data.customerId,
            paymentTerms: data.paymentTerms || undefined,
            notes: data.notes || data.internalNotes || '',
            terms: data.terms || ''
          });
          // 選中客戶，解除按鈕禁用
          const foundCustomer = (globalAny.__invoiceCustomersCache || []).find((c: Customer) => c.id === String(data.customerId));
          if (foundCustomer) setSelectedCustomer(foundCustomer);
          else if (data.customer) {
            setSelectedCustomer({ id: String(data.customer.id), name: data.customer.name, email: data.customer.email, phone: data.customer.phone, address: data.customer.address } as Customer);
          }
          setInvoice(prev => ({
            ...prev,
            id: String(data.id),
            invoiceNumber: data.invoiceNo || data.invoiceNumber,
            customerId: data.customerId,
            customerName: data.customer?.name || '',
            items: (data.items||[]).map((it:any)=>({
              id: String(it.id),
              productId: String(it.productId),
              productName: it.product?.itemName || '',
              description: it.description || '',
              quantity: Number(it.quantity||0),
              unitPrice: Number(it.unitPrice||0),
              discount: 0,
              taxRate: 0,
              amount: Number(it.lineTotal||0)
            })),
            subtotal: Number(data.totalAmount||0),
            taxAmount: 0,
            discountAmount: 0,
            totalAmount: Number(data.totalAmount||0)
          }));
          setIsEdit(true);
          // 使用後清除標記
          localStorage.removeItem('invoice.editId');
        }
      } catch (e) {
        setCustomers([]); setProducts([]);
      }
    };
    init();
  }, [form]);

  const handleCustomerSelect = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setInvoice(prev => ({
        ...prev,
        customerId: customer.id,
        customerName: customer.name
      }));
    }
  };

  const addInvoiceItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      productId: '',
      productName: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      taxRate: 10,
      amount: 0
    };

    setInvoice(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateInvoiceItem = (itemId: string, field: keyof InvoiceItem, value: any) => {
    setInvoice(prev => {
      const items = prev.items.map(item => {
        if (item.id === itemId) {
          const updatedItem = { ...item, [field]: value };
          
          // Auto-populate product details
          if (field === 'productId') {
            const product = products.find(p => p.id === value);
            if (product) {
              updatedItem.productName = product.name;
              updatedItem.description = product.description;
              updatedItem.unitPrice = product.price;
              updatedItem.taxRate = product.taxRate;
            }
          }
          
          // Calculate amount
          const subtotal = updatedItem.quantity * updatedItem.unitPrice;
          const discountAmount = (subtotal * updatedItem.discount) / 100;
          updatedItem.amount = subtotal - discountAmount + ((subtotal - discountAmount) * updatedItem.taxRate / 100);
          
          return updatedItem;
        }
        return item;
      });

      // Recalculate totals
      const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
      const discountAmount = items.reduce((sum, item) => sum + ((item.quantity * item.unitPrice * item.discount) / 100), 0);
      const taxAmount = items.reduce((sum, item) => {
        const itemSubtotal = (item.quantity * item.unitPrice) - ((item.quantity * item.unitPrice * item.discount) / 100);
        return sum + (itemSubtotal * item.taxRate / 100);
      }, 0);
      const totalAmount = subtotal - discountAmount + taxAmount;

      return {
        ...prev,
        items,
        subtotal,
        discountAmount,
        taxAmount,
        totalAmount
      };
    });
  };

  const removeInvoiceItem = (itemId: string) => {
    setInvoice(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const handleSaveInvoice = async (status: 'draft' | 'sent') => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      
      const invoiceData = {
        ...invoice,
        ...values,
        status
      };

      // 後端保存
      const payload: any = {
        customerId: invoiceData.customerId,
        userId: localStorage.getItem('current-user-id') || localStorage.getItem('userId'),
        companyId: localStorage.getItem('current-company-id') || localStorage.getItem('companyId'),
        dueDate: values.dueDate?.toISOString?.() || undefined,
        status: status === 'sent' ? 'SENT' : 'DRAFT',
        paymentTerms: values.paymentTerms,
        notes: values.notes,
        terms: values.terms,
        items: invoice.items.map(it => ({ productId: it.productId, quantity: it.quantity, unitPrice: it.unitPrice, description: it.description }))
      };
      if (isEdit && invoice.id) {
        await apiService.updateInvoice(invoice.id, payload);
      } else {
        await apiService.createInvoice(payload);
      }
      
      message.success(`Invoice ${status === 'draft' ? 'saved as draft' : 'sent'} successfully!`);
      
      // 優先使用父層回調（彈窗場景：關閉並刷新列表）；否則退回到頁面導航方式
      if (onSaved) {
        onSaved();
      } else {
        localStorage.setItem('sales.activeTab', 'invoices');
        localStorage.setItem('sales.refreshInvoices', '1');
        navigate('/sales/invoices');
      }
    } catch (error) {
      message.error('Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  const itemColumns: ColumnsType<InvoiceItem> = [
    {
      title: 'Product/Service',
      dataIndex: 'productId',
      key: 'productId',
      width: 200,
      render: (value, record) => (
        <Select
          value={value}
          onChange={(val) => updateInvoiceItem(record.id, 'productId', val)}
          placeholder="Select product"
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) => {
            const product = products.find(p => p.id === option?.value);
            if (!product) return false;

            // 检查产品名称、SKU或备用SKU是否匹配搜索词
            const searchLower = input.toLowerCase();
            const nameMatch = product.name.toLowerCase().includes(searchLower);
            const skuMatch = [product.sku, ...product.alternateSkuList??[]].some(sku => sku.toLowerCase().includes(searchLower));

            return nameMatch || skuMatch || false;
          }}
        >
          {products.map(product => (
            <Option key={product.id} value={product.id}>
              {product.name} - ${product.price}/{product.unit}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (value, record) => (
        <Input.TextArea
          value={value}
          onChange={(e) => updateInvoiceItem(record.id, 'description', e.target.value)}
          placeholder="Item description"
          rows={1}
        />
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => updateInvoiceItem(record.id, 'quantity', val || 0)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 120,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => updateInvoiceItem(record.id, 'unitPrice', val || 0)}
          min={0}
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value!.replace(/\$\s?|(,*)/g, '')}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Discount %',
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => updateInvoiceItem(record.id, 'discount', val || 0)}
          min={0}
          max={100}
          formatter={value => `${value}%`}
          parser={value => value!.replace('%', '')}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Tax %',
      dataIndex: 'taxRate',
      key: 'taxRate',
      width: 80,
      render: (value, record) => (
        <InputNumber
          value={value}
          onChange={(val) => updateInvoiceItem(record.id, 'taxRate', val || 0)}
          min={0}
          max={100}
          formatter={value => `${value}%`}
          parser={value => value!.replace('%', '')}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (value) => (
        <Text strong>${value.toFixed(2)}</Text>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeInvoiceItem(record.id)}
        />
      ),
    },
  ];

  const InvoicePreview = () => (
    <Modal
      title="Invoice Preview"
      open={previewVisible}
      onCancel={() => setPreviewVisible(false)}
      width={800}
      footer={[
        <Button key="print" icon={<PrinterOutlined />} onClick={() => {
          // 開新視窗寫入 HTML 再列印（相容各瀏覽器，亦可存成 PDF）
          const buildHTML = () => {
            const itemsRows = (invoice.items || []).map(it => `
              <tr>
                <td style=\"padding:8px;border-bottom:1px solid #eee\">${it.productName || ''}</td>
                <td style=\"padding:8px;border-bottom:1px solid #eee;text-align:center\">${it.quantity}</td>
                <td style=\"padding:8px;border-bottom:1px solid #eee;text-align:right\">$${Number(it.unitPrice||0).toFixed(2)}</td>
                <td style=\"padding:8px;border-bottom:1px solid #eee;text-align:right\">$${Number(it.amount||0).toFixed(2)}</td>
              </tr>`).join('');
            return `<!DOCTYPE html><html><head><meta charset='utf-8'>
              <title>${invoice.invoiceNumber || 'INVOICE'}</title>
              <style>
                body{font-family:Arial,Helvetica,sans-serif;color:#333;padding:24px}
                h1{color:#1890ff;margin:0 0 8px 0}
                .row{display:flex;justify-content:space-between;margin-bottom:16px}
                .box{width:48%}
                table{width:100%;border-collapse:collapse;margin-top:12px}
                th{background:#f5f5f5;text-align:left;padding:8px}
              </style>
            </head><body>
              <h1>INVOICE</h1>
              <div>${invoice.invoiceNumber || ''}</div>
              <div class='row'>
                <div class='box'>
                  <div><strong>Bill To:</strong></div>
                  <div><strong>${selectedCustomer?.name || ''}</strong></div>
                  <div>${selectedCustomer?.address || ''}</div>
                  <div>${selectedCustomer?.email || ''}</div>
                  <div>${selectedCustomer?.phone || ''}</div>
                </div>
                <div class='box' style='text-align:right'>
                  <div><strong>Issue Date:</strong> ${invoice.issueDate || ''}</div>
                  <div><strong>Due Date:</strong> ${invoice.dueDate || ''}</div>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style='text-align:center'>Qty</th>
                    <th style='text-align:right'>Rate</th>
                    <th style='text-align:right'>Amount</th>
                  </tr>
                </thead>
                <tbody>${itemsRows}</tbody>
              </table>
              <div style='margin-top:16px;text-align:right'>
                <div>Subtotal: <strong>$${invoice.subtotal.toFixed(2)}</strong></div>
                <div>Discount: <strong>-$${invoice.discountAmount.toFixed(2)}</strong></div>
                <div>Tax: <strong>$${invoice.taxAmount.toFixed(2)}</strong></div>
                <div style='margin-top:8px;font-size:16px'>Total: <strong style='color:#1890ff'>$${invoice.totalAmount.toFixed(2)}</strong></div>
              </div>
            </body></html>`;
          };
          const html = buildHTML();
          const printWindow = window.open('', '_blank');
          if (!printWindow) return;
          printWindow.document.open();
          printWindow.document.write(html);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => { printWindow.print(); /* printWindow.close(); */ }, 200);
        }}>
          Print
        </Button>,
        <Button key="download" type="primary" onClick={() => {
          // 採 Inbound 相同做法：以新視窗開 HTML 並由使用者選擇「另存為 PDF」
          const buildHTML = () => {
            const itemsRows = (invoice.items || []).map(it => `
              <tr>
                <td style=\"padding:8px;border-bottom:1px solid #eee\">${it.productName || ''}</td>
                <td style=\"padding:8px;border-bottom:1px solid #eee;text-align:center\">${it.quantity}</td>
                <td style=\"padding:8px;border-bottom:1px solid #eee;text-align:right\">$${Number(it.unitPrice||0).toFixed(2)}</td>
                <td style=\"padding:8px;border-bottom:1px solid #eee;text-align:right\">$${Number(it.amount||0).toFixed(2)}</td>
              </tr>`).join('');
            return `<!DOCTYPE html><html><head><meta charset='utf-8'>
              <title>${invoice.invoiceNumber || 'INVOICE'}</title>
              <style>
                body{font-family:Arial,Helvetica,sans-serif;color:#333;padding:24px}
                h1{color:#1890ff;margin:0 0 8px 0}
                .row{display:flex;justify-content:space-between;margin-bottom:16px}
                .box{width:48%}
                table{width:100%;border-collapse:collapse;margin-top:12px}
                th{background:#f5f5f5;text-align:left;padding:8px}
              </style>
            </head><body>
              <h1>INVOICE</h1>
              <div>${invoice.invoiceNumber || ''}</div>
              <div class='row'>
                <div class='box'>
                  <div><strong>Bill To:</strong></div>
                  <div><strong>${selectedCustomer?.name || ''}</strong></div>
                  <div>${selectedCustomer?.address || ''}</div>
                  <div>${selectedCustomer?.email || ''}</div>
                  <div>${selectedCustomer?.phone || ''}</div>
                </div>
                <div class='box' style='text-align:right'>
                  <div><strong>Issue Date:</strong> ${invoice.issueDate || ''}</div>
                  <div><strong>Due Date:</strong> ${invoice.dueDate || ''}</div>
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style='text-align:center'>Qty</th>
                    <th style='text-align:right'>Rate</th>
                    <th style='text-align:right'>Amount</th>
                  </tr>
                </thead>
                <tbody>${itemsRows}</tbody>
              </table>
              <div style='margin-top:16px;text-align:right'>
                <div>Subtotal: <strong>$${invoice.subtotal.toFixed(2)}</strong></div>
                <div>Discount: <strong>-$${invoice.discountAmount.toFixed(2)}</strong></div>
                <div>Tax: <strong>$${invoice.taxAmount.toFixed(2)}</strong></div>
                <div style='margin-top:8px;font-size:16px'>Total: <strong style='color:#1890ff'>$${invoice.totalAmount.toFixed(2)}</strong></div>
              </div>
            </body></html>`;
          };
          const html = buildHTML();
          const printWindow = window.open('', '_blank');
          if (!printWindow) return;
          printWindow.document.open();
          printWindow.document.write(html);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => { printWindow.print(); /* 使用者可選擇另存為 PDF */ }, 200);
        }}>
          Download PDF
        </Button>
      ]}
    >
      <div style={{ padding: '20px', backgroundColor: 'white' }}>
        <div id="invoice-preview-content">
        <Row justify="space-between" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ color: '#1890ff' }}>INVOICE</Title>
            <Text strong>{invoice.invoiceNumber}</Text>
          </Col>
          <Col style={{ textAlign: 'right' }}>
            <div><Text strong>Issue Date:</Text> {invoice.issueDate}</div>
            <div><Text strong>Due Date:</Text> {invoice.dueDate}</div>
          </Col>
        </Row>

        <Row gutter={48} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Title level={5}>Bill To:</Title>
            {selectedCustomer && (
              <div>
                <div><Text strong>{selectedCustomer.name}</Text></div>
                <div>{selectedCustomer.address}</div>
                <div>{selectedCustomer.email}</div>
                <div>{selectedCustomer.phone}</div>
                {selectedCustomer.taxId && <div>Tax ID: {selectedCustomer.taxId}</div>}
              </div>
            )}
          </Col>
          <Col span={12}>
            <Title level={5}>From:</Title>
            <div>
              <div><Text strong>Your Company Name</Text></div>
              <div>123 Business Street</div>
              <div>City, State 12345</div>
              <div>contact@company.com</div>
            </div>
          </Col>
        </Row>

        <Table
          dataSource={invoice.items}
          columns={[
            { title: 'Description', dataIndex: 'productName', key: 'productName' },
            { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 60 },
            { title: 'Rate', dataIndex: 'unitPrice', key: 'unitPrice', width: 80, render: val => `$${val.toFixed(2)}` },
            { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 100, render: val => `$${val.toFixed(2)}` }
          ]}
          pagination={false}
          size="small"
        />

        <Row justify="end" style={{ marginTop: 24 }}>
          <Col span={8}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ marginBottom: 8 }}>
                <Text>Subtotal: </Text>
                <Text strong>${invoice.subtotal.toFixed(2)}</Text>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text>Discount: </Text>
                <Text strong>-${invoice.discountAmount.toFixed(2)}</Text>
              </div>
              <div style={{ marginBottom: 8 }}>
                <Text>Tax: </Text>
                <Text strong>${invoice.taxAmount.toFixed(2)}</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ fontSize: 16 }}>Total: ${invoice.totalAmount.toFixed(2)}</Text>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>
    </Modal>
  );

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <Card style={{ marginBottom: '24px' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ margin: 0 }}>
                <ShoppingCartOutlined style={{ marginRight: 8 }} />
                {isEdit ? '編輯發票' : '建立發票'}
              </Title>
              <Text type="secondary">
                {isEdit ? '編輯與管理客戶發票' : '建立並管理客戶發票'}
              </Text>
            </Col>
            <Col>
          <Space>
            <Button 
              icon={<EyeOutlined />}
              onClick={() => setPreviewVisible(true)}
              disabled={invoice.items.length === 0}
            >
              預覽
            </Button>
            <Button 
              icon={<SaveOutlined />}
              onClick={() => handleSaveInvoice('draft')}
              loading={loading}
            >
              儲存草稿
            </Button>
            <Button 
              type="primary"
              icon={<SendOutlined />}
              onClick={() => handleSaveInvoice('sent')}
              loading={loading}
              disabled={!selectedCustomer || invoice.items.length === 0}
            >
              發送發票
            </Button>
          </Space>
            </Col>
          </Row>
        </Card>

      <Form form={form} layout="vertical">
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <Card title="發票資訊" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="invoiceNumber"
                    label="發票號碼"
                    rules={[{ required: true, message: '請填寫發票號碼' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="issueDate"
                    label="開立日期"
                    rules={[{ required: true, message: '請選擇開立日期' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="dueDate"
                    label="到期日"
                    rules={[{ required: true, message: '請選擇到期日' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="customerId"
                    label="客戶"
                    rules={[{ required: true, message: '請選擇客戶' }]}
                  >
                    <Select
                      placeholder="選擇客戶"
                      onChange={handleCustomerSelect}
                      showSearch
                      optionFilterProp="children"
                    >
                      {customers.map(customer => (
                        <Option key={customer.id} value={customer.id}>
                          <Text strong>{customer.name}</Text>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="paymentTerms" label="付款條件">
                    <Select placeholder="選擇付款條件">
                      <Option value="net15">Net 15</Option>
                      <Option value="net30">Net 30</Option>
                      <Option value="net45">Net 45</Option>
                      <Option value="due_on_receipt">收到即付</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card 
              title="發票項目" 
              extra={
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={addInvoiceItem}
                >
                  新增項目
                </Button>
              }
            >
              <Table
                dataSource={invoice.items}
                columns={itemColumns}
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: (
                    <div style={{ padding: 40, textAlign: 'center' }}>
                      <ShoppingCartOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                      <div>尚未新增項目</div>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={addInvoiceItem}
                        style={{ marginTop: 16 }}
                      >
                        新增第一個項目
                      </Button>
                    </div>
                  )
                }}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="發票摘要" style={{ marginBottom: 24 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Row justify="space-between">
                  <Text>小計：</Text>
                  <Text strong>${invoice.subtotal.toFixed(2)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>折扣：</Text>
                  <Text strong>-${invoice.discountAmount.toFixed(2)}</Text>
                </Row>
                <Row justify="space-between">
                  <Text>稅額：</Text>
                  <Text strong>${invoice.taxAmount.toFixed(2)}</Text>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <Row justify="space-between">
                  <Text strong style={{ fontSize: 16 }}>總計：</Text>
                  <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                    ${invoice.totalAmount.toFixed(2)}
                  </Text>
                </Row>
              </Space>
            </Card>

            <Card title="其他資訊">
              <Form.Item name="notes" label="Notes">
                <TextArea 
                  rows={3} 
                  placeholder="可填寫給客戶的備註..."
                />
              </Form.Item>
              <Form.Item name="terms" label="條款與條件">
                <TextArea 
                  rows={3} 
                  placeholder="付款條件、退貨政策等..."
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>

        <InvoicePreview />
      </div>
    </div>
  );
};

export default InvoiceBuilder; 